import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import cn from "clsx";
import { isFunction } from "../../utils/helpers";
import useT from "../../hooks/useT";
import Button from "../Button";
import api from "../../utils/api";
import { CustomerPageContext, DQEContext } from "../../containers/CustomerPage";
import { setStoredItem } from "../../utils/localStorage";
import {
  codesValidtion,
  getTransaltion,
  TranslationKey
} from "./transaltionMapper";

type InternalRefObject =
  | React.MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null>
  | React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>;

export interface InputEmailDqeProps {
  /** Allows using `...inputProps` to pass HTMLInputElement/HTMLTextareaElement props to the input/text element itself */
  [propName: string]: any;
  id: string;
  name: string;
  value: string | null;
  disabled?: boolean;
  clearable?: boolean;
  className?: string;
  placeholder?: string;
  onFocus?: React.FocusEventHandler;
  onBlur?: React.FocusEventHandler;
  onChange?: React.ChangeEventHandler;
  onClear?: React.MouseEventHandler;
  onCustomError?: (error: string) => void;
  onClearCustomError?: () => void;
  onCustomLable?: (text: string) => void;
  onClaerCustomLabel?: () => void;
}
// export function validateEmailFormat(email: string): boolean {
//   const emailRegex = /^\s*[\w\-\+_]+(\.[\w\-\+_]+)*\@[\w\-\+_]+\.[\w\-\+_]+(\.[\w\-\+_]+)*\s*$/;
//   return emailRegex.test(email);
// }

export const InputEmailDqe = React.forwardRef(
  (
    {
      id,
      name,
      type,
      value,
      disabled,
      clearable,
      className,
      placeholder,
      onFocus,
      onBlur,
      onChange,
      onClear,
      onCustomError,
      onClearCustomError,
      onCustomLable,
      onClaerCustomLabel,
      ...inputProps
    }: InputEmailDqeProps,
    forwardedRef?: React.Ref<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { t } = useT();
    let _value = value ? value : "";
    const internalRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(
      null
    );
    const [isValidEmailFormat, setIsValidEmailFormat] = useState<boolean>(
      false
    );
    const [touch, setTouch] = useState<boolean>(false);
    const [customerPage, _] = useContext(CustomerPageContext);

    const { DQEDispatch } = useContext<any>(DQEContext);

    const ref: InternalRefObject =
      !forwardedRef || isFunction(forwardedRef)
        ? internalRef
        : (forwardedRef as InternalRefObject);

    const handleChangeEmail = (event: any) => {
      event.stopPropagation();
      if (onClaerCustomLabel) onClaerCustomLabel();
      const email = event.target.value || {};

      setCurrent(email);
      if (onChange) {
        onChange(event);
      }
    };

    useEffect(() => {
      if (!isValidEmailFormat && touch) {
        activateLables(
          onCustomError as Function,
          "pattern could not be validated"
        );
        claerLables(onCustomLable as Function);
      }
    }, [isValidEmailFormat]);

    function activateLables(functionToActivate: Function, translation: string) {
      if (functionToActivate) {
        functionToActivate(t(translation, { lng: customerPage.language }));
      }
    }

    function claerLables(functionToClear: Function) {
      if (functionToClear) {
        functionToClear();
      }
    }
    function activateLablesForTranslation(translation: TranslationKey) {
      switch (translation.emailStatus) {
        case codesValidtion.valid:
          DQEDispatch({ type: "UPDATE_EMAIL_VALID", payload: true });
          claerLables(onClearCustomError as Function);
          activateLables(onCustomLable as Function, translation.translationKey);
          return;
        case codesValidtion.invalid:
          DQEDispatch({ type: "UPDATE_EMAIL_VALID", payload: false });
          claerLables(onClearCustomError as Function);
          activateLables(onCustomLable as Function, translation.translationKey);
          return;
        case codesValidtion.formBlocking:
          DQEDispatch({ type: "UPDATE_EMAIL_VALID", payload: false });
          claerLables(onCustomLable as Function);
          activateLables(onCustomError as Function, translation.translationKey);
          return;
        case codesValidtion.blank:
          DQEDispatch({ type: "UPDATE_EMAIL_VALID", payload: false });
          claerLables(onCustomLable as Function);
          activateLables(onCustomError as Function, translation.translationKey);
          return;
      }
    }
    const sendToValadate = async (email: string) => {
      if (email === "") {
        claerLables(onCustomLable as Function);
        claerLables(onClearCustomError as Function);
        DQEDispatch({ type: "RESET" });
        return;
      }

      const response = await api.validateMail(email);
      const translation = getTransaltion(response.IdError, email);
      activateLablesForTranslation(translation as TranslationKey);
    };

    const setCurrent = async (email: any) => {
      _value = email;
    };

    const handleClickClear = useCallback(
      (event: React.MouseEvent) => {
        event.preventDefault();
        if (onClear) {
          onClear(event);
        }
      },
      [onClear, name]
    );

    useEffect(() => {
      // Special reset for date input type due to iOS not clearing
      // the value.
    }, [type, _value]);

    return (
      <div
        className={cn(
          "input input-text",
          className,
          !!value && "has-value",
          !!clearable && "is-clearable"
        )}
      >
        <input
          type="email"
          {...inputProps}
          id={id}
          name={name}
          value={_value}
          disabled={disabled}
          onFocus={onFocus}
          onBlur={e => {
            if (onBlur) {
              onBlur(e);
              sendToValadate(_value);
            }
          }}
          onChange={handleChangeEmail}
          placeholder={placeholder}
          className="input-control input-text-control"
          ref={ref as React.RefObject<HTMLInputElement>}
          onClick={() => setTouch(true)}
        />

        {!!_value && clearable ? (
          <div className="input-actions input-text-actions">
            <Button
              icon="clear"
              ariaLabel={t("general.buttons.clear.label")}
              className="clear-input"
              onClick={handleClickClear}
            />
          </div>
        ) : null}
      </div>
    );
  }
);

InputEmailDqe.displayName = "InputText";

export default InputEmailDqe;
